// useSoftphone — single source of truth for call sessions in the workspace.
// Maintains its own WebSocket subscription on /api/ws/agent and exposes
// session state + actions for the 12 call controls.
//
// Backend frames consumed:
//   { type: "call.new",    session }
//   { type: "call.state",  session }
//   { type: "call.ended",  session, cdr }
import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const RAW_BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const WS_BASE = RAW_BACKEND.replace(/^http/, "ws");

const OPEN_STATES = new Set(["dialing", "ringing", "talking", "held", "wrapping"]);

export function useSoftphone() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);    // open call sessions
  const [extension, setExtension] = useState("");
  const [recentCdr, setRecentCdr] = useState(null);
  const wsRef = useRef(null);
  const lastBridgedRef = useRef({ key: "", at: 0 });

  // Initial fetch
  useEffect(() => {
    if (!user) return;
    api.get("/softphone/state").then((r) => {
      setSessions(r.data.sessions || []);
      setExtension(r.data.extension || "");
    }).catch(() => {});
  }, [user?.id]);

  // WebSocket subscription
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("c7_token");
    if (!token) return;
    let stopped = false;
    let reconnect = null;

    function connect() {
      if (stopped) return;
      const ws = new WebSocket(`${WS_BASE}/api/ws/agent?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        if (e.data === "pong") return;
        let msg; try { msg = JSON.parse(e.data); } catch { return; }
        // ─── Predictive dialer bridge event ────────────────────────────
        // The customer leg just joined THIS agent's ConfBridge → fire a
        // CRM screen-pop event. The agent's audio is already live (their
        // off-hook WebRTC leg was parked in the same bridge).
        //
        // Slice 3.5i — match by EITHER agent_id (uuid) OR conf name
        // (cb_<tenant>_<my-ext>). The conf-name match is the more reliable
        // guarantee that "this bridge IS my parked seat", and survives
        // any drift between dialer_inflight.agent_id and the active
        // JWT's `sub` field (we hit this in production when the admin's
        // own agent doc id != JWT sub for super_super_admin accounts).
        if (msg.type === "dialer.bridged") {
          const idMatch = msg.agent_id && msg.agent_id === user.id;
          const myExt = String(user.extension || "");
          const extDirectMatch = myExt && msg.agent_ext && String(msg.agent_ext) === myExt;
          const extConfMatch = myExt && typeof msg.conf === "string"
            && msg.conf.endsWith(`_${myExt}`);
          const extMatch = extDirectMatch || extConfMatch;
          // eslint-disable-next-line no-console
          console.info("[c7] dialer.bridged received", {
            agent_id: msg.agent_id, my_id: user.id,
            agent_ext: msg.agent_ext, my_ext: myExt,
            conf: msg.conf,
            idMatch, extDirectMatch, extConfMatch,
            phone: msg.phone, lead_id: msg.lead_id,
          });
          if (!idMatch && !extMatch) {
            // eslint-disable-next-line no-console
            console.warn("[c7] dialer.bridged DROPPED — neither agent_id nor agent_ext matches this user. Screen-pop will NOT fire.");
          }
          if (idMatch || extMatch) {
            // De-dupe: the screen-pop is broadcast twice for resilience
            // (direct on StasisStart + on ChannelEnteredBridge). Pop once.
            // `connected_at` (inbound ACD) differs per AgentConnect so a call
            // that is transferred to a queue and routed BACK to the same agent
            // re-pops the CRM instead of being wrongly deduped as the same call.
            const key = `${msg.channel || ""}|${msg.lead_id || ""}|${msg.conf || ""}|${msg.connected_at || ""}`;
            const nowMs = Date.now();
            if (lastBridgedRef.current.key === key
                && (nowMs - lastBridgedRef.current.at) < 15000) {
              // eslint-disable-next-line no-console
              console.info("[c7] dialer.bridged duplicate ignored", { key });
            } else {
              lastBridgedRef.current = { key, at: nowMs };
              try {
                window.dispatchEvent(new CustomEvent("dialer:bridged", {
                  detail: {
                    lead_id: msg.lead_id,
                    phone:   msg.phone || "",
                    name:    msg.name  || "",
                    run_id:  msg.run_id,
                    conf:    msg.conf,
                    landed:  true,
                  },
                }));
              } catch {}
            }
          }
          // Don't return — also let any session-tied logic below run.
        }
        // ─── Predictive dialer END event — customer hung up first ─────
        // Slice 3.5i (round 4) — fire an auto-wrap-up event so the agent
        // can disposition the call even when the customer drops it. The
        // backend's ChannelLeftBridge handler enriches the payload with
        // phone/name/conf/timestamps so we can build a synthetic CDR
        // here without any extra round-trip.
        if (msg.type === "dialer.ended") {
          const myExt = String(user.extension || "");
          const idMatch = msg.agent_id && msg.agent_id === user.id;
          const extMatch = (msg.agent_ext && myExt && msg.agent_ext === myExt)
            || (myExt && typeof msg.conf === "string"
                && msg.conf.endsWith(`_${myExt}`));
          // eslint-disable-next-line no-console
          console.info("[c7] dialer.ended received", {
            agent_id: msg.agent_id, my_id: user.id,
            conf: msg.conf, my_ext: myExt,
            idMatch, extMatch, bridged: msg.bridged,
            ended_by: msg.ended_by,
          });
          if (idMatch || extMatch) {
            // Compute duration / billable seconds from timestamps
            const startTs = msg.originated_at ? Date.parse(msg.originated_at) : 0;
            const ansTs   = msg.bridged_at    ? Date.parse(msg.bridged_at)    : 0;
            const endTs   = msg.ended_at      ? Date.parse(msg.ended_at)      : Date.now();
            const duration = startTs ? Math.max(0, Math.round((endTs - startTs) / 1000)) : 0;
            const billable = ansTs   ? Math.max(0, Math.round((endTs - ansTs)   / 1000)) : 0;
            const cdr = {
              call_id:           msg.channel,
              peer:              msg.phone || "",
              peer_name:         msg.name  || msg.phone || "",
              direction:         msg.direction || "outbound",
              started_at:        msg.originated_at,
              answered_at:       msg.bridged_at,
              ended_at:          msg.ended_at,
              duration_seconds:  duration,
              billable_seconds:  billable,
              campaign_id:       msg.campaign_id,
              lead_id:           msg.lead_id,
              queue_id:          msg.queue_id || null,
              is_live:           false,
              is_dialer_call:    true,
              is_inbound_dialer: !!msg.is_inbound_dialer,
              cdr_id:            msg.cdr_id || null,
            };
            try {
              window.dispatchEvent(new CustomEvent("dialer:customer-hangup", {
                detail: { cdr, ended_by: msg.ended_by || "customer_hangup" },
              }));
            } catch {}
          }
          // Don't return — let session-tied logic below run too.
        }
        if (!msg.session || msg.session.agent_id !== user.id) {
          // Only react to MY call frames
          return;
        }
        if (msg.type === "call.new") {
          setSessions((cur) => {
            if (cur.find((s) => s.id === msg.session.id)) return cur;
            // Toast on incoming ring
            if (msg.session.direction === "inbound") {
              toast.message("Incoming call", { description: msg.session.peer_name || msg.session.peer });
            }
            return [...cur, msg.session];
          });
          // CRM screen-pop for inbound calls too — same path as predictive-dialer
          // bridge, so the agent's workspace always shows who they're talking to.
          if (msg.session.direction === "inbound" && msg.session.peer) {
            try {
              window.dispatchEvent(new CustomEvent("dialer:bridged", {
                detail: {
                  phone: msg.session.peer,
                  name:  msg.session.peer_name || "",
                  source: "inbound",
                },
              }));
            } catch {}
          }
        } else if (msg.type === "call.state") {
          setSessions((cur) => {
            const found = cur.find((s) => s.id === msg.session.id);
            if (!found) {
              // New session this client didn't know about
              if (OPEN_STATES.has(msg.session.state)) return [...cur, msg.session];
              return cur;
            }
            return cur.map((s) => s.id === msg.session.id ? msg.session : s);
          });
        } else if (msg.type === "call.ended") {
          setSessions((cur) => cur.filter((s) => s.id !== msg.session.id));
          setRecentCdr(msg.cdr || null);
          // NOTE: dialer:ended is dispatched by the wrap-up dialog AFTER
          // the agent picks a disposition — not here. This keeps the
          // CRM screen visible while the mandatory wrap-up is in progress.
        } else if (msg.type === "channel.inbound") {
          // A new omnichannel message arrived (IG/FB/Telegram via PostProxy
          // sync, voicemail, or a simulator) → tell the inbox to refresh.
          try {
            window.dispatchEvent(new CustomEvent("channel:inbound", { detail: msg }));
          } catch {}
        } else if (msg.type === "conversation.pending" || msg.type === "conversation.claimed"
                   || msg.type === "conversation.bot_reply" || msg.type === "conversation.updated") {
          // AI Orchestration state changes → refresh the claim pool + inbox.
          try {
            window.dispatchEvent(new CustomEvent("orch:update", { detail: msg }));
          } catch {}
        } else if (msg.type === "channel.config_updated") {
          // An admin enabled/disabled or reconfigured a channel → tell the
          // workspace to refresh its live channel list (switcher chips).
          try {
            window.dispatchEvent(new CustomEvent("channel:config", { detail: msg }));
          } catch {}
        } else if (msg.type === "message.status") {
          // A delivery receipt (Viber delivered/seen/failed) → update the tick
          // on the matching outbound message in the open thread.
          try {
            window.dispatchEvent(new CustomEvent("message:status", { detail: msg }));
          } catch {}
        } else if (msg.type === "callback.missed") {
          // A due scheduled callback couldn't auto-dial (no agent ready).
          // Alert the scheduling agent + supervisors/admins.
          try {
            window.dispatchEvent(new CustomEvent("callback:missed", { detail: msg }));
          } catch {}
          const role = user?.role || "";
          const isSup = ["supervisor", "admin", "super_admin", "super_super_admin"].includes(role);
          const isOwner = msg.agent_id && msg.agent_id === user?.id;
          if (isSup || isOwner) {
            const who = msg.customer || msg.phone || "a customer";
            const why = msg.reason === "no_active_trunk"
              ? "no active trunk"
              : "no agent was available";
            try {
              toast.warning(`Missed callback: ${who} — ${why} at the scheduled time`, { duration: 10000 });
            } catch {}
          }
        }
      };      ws.onclose = () => { if (!stopped) reconnect = setTimeout(connect, 2500); };
      ws.onerror = () => { try { ws.close(); } catch {} };
    }
    connect();
    return () => {
      stopped = true;
      if (reconnect) clearTimeout(reconnect);
      try { wsRef.current?.close(); } catch {}
    };
  }, [user?.id]);

  // Actions
  const post = useCallback(async (path, body) => {
    try {
      const r = await api.post(path, body || {});
      // Optimistic update — server WS will also push
      if (r.data?.session) {
        setSessions((cur) => {
          const open = OPEN_STATES.has(r.data.session.state);
          if (!open) return cur.filter((s) => s.id !== r.data.session.id);
          if (!cur.find((s) => s.id === r.data.session.id)) return [...cur, r.data.session];
          return cur.map((s) => s.id === r.data.session.id ? r.data.session : s);
        });
      }
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
      throw e;
    }
  }, []);

  const originate = useCallback(async (destination, peer_name) => {
    // Fire CRM screen-pop IMMEDIATELY (while the call rings) so the agent
    // sees who they're calling before the customer picks up. Unknown
    // numbers get an auto-created contact handled in Workspace.jsx.
    if (destination) {
      try {
        window.dispatchEvent(new CustomEvent("dialer:bridged", {
          detail: {
            phone: destination,
            name:  peer_name || "",
            source: "manual_dial",
          },
        }));
      } catch {}
    }
    return post("/softphone/originate", { destination, peer_name });
  }, [post]);
  const simulateInbound = useCallback((caller, caller_name) => post("/softphone/inbound", { caller, caller_name }), [post]);
  const answer = useCallback((id) => post(`/softphone/${id}/answer`), [post]);
  const hangup = useCallback((id) => post(`/softphone/${id}/hangup`), [post]);
  const hold = useCallback((id) => post(`/softphone/${id}/hold`), [post]);
  const unhold = useCallback((id) => post(`/softphone/${id}/unhold`), [post]);
  const mute = useCallback((id) => post(`/softphone/${id}/mute`), [post]);
  const unmute = useCallback((id) => post(`/softphone/${id}/unmute`), [post]);
  const dtmf = useCallback((id, digits) => post(`/softphone/${id}/dtmf`, { digits }), [post]);
  const recordingToggle = useCallback((id) => post(`/softphone/${id}/recording/toggle`), [post]);
  const transferBlind = useCallback((id, target_ext, target_name) => post(`/softphone/${id}/transfer/blind`, { target_ext, target_name }), [post]);
  const transferAttendedStart = useCallback((id, target_ext, target_name) => post(`/softphone/${id}/transfer/attended/start`, { target_ext, target_name }), [post]);
  const transferAttendedComplete = useCallback((id) => post(`/softphone/${id}/transfer/attended/complete`), [post]);
  const conferenceAdd = useCallback((id, target_ext, target_name) => post(`/softphone/${id}/conference/add`, { target_ext, target_name }), [post]);
  const voicemailDrop = useCallback((id, audio_id) => post(`/softphone/${id}/voicemail-drop`, { audio_id }), [post]);
  const wrapup = useCallback((id, disposition_id, notes) => post(`/softphone/${id}/wrapup`, { disposition_id, notes }), [post]);

  return {
    sessions, extension, recentCdr,
    originate, simulateInbound, answer, hangup,
    hold, unhold, mute, unmute, dtmf, recordingToggle,
    transferBlind, transferAttendedStart, transferAttendedComplete,
    conferenceAdd, voicemailDrop, wrapup,
  };
}
